/**
 * Olfacy product card behaviour (deferred module, no dependencies).
 * - Wishlist hearts: saved in localStorage, synced across cards and tabs.
 * - Add to Bag: adds via AJAX, then lets Horizon's cart drawer/cart count update
 *   through Shopify's standard cart event, and opens the drawer.
 * Without JS the Add to Bag form still works (normal POST to /cart/add).
 */

const WISHLIST_KEY = 'olfacy:wishlist';
const WISHLIST_EVENT = 'olfacy:wishlist-change';

const readWishlist = () => {
  try {
    const list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

const writeWishlist = (list) => {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  } catch {}
  document.dispatchEvent(new CustomEvent(WISHLIST_EVENT, { detail: { handles: list } }));
};

class OlfacyWishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.sync = () => this.render();
    this.button.addEventListener('click', this.toggle);
    document.addEventListener(WISHLIST_EVENT, this.sync);
    window.addEventListener('storage', this.onStorage);
    this.render();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.toggle);
    document.removeEventListener(WISHLIST_EVENT, this.sync);
    window.removeEventListener('storage', this.onStorage);
  }

  onStorage = (event) => {
    if (event.key === WISHLIST_KEY) this.render();
  };

  toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const handle = this.dataset.handle;
    const list = readWishlist();
    const index = list.indexOf(handle);
    if (index === -1) list.unshift(handle);
    else list.splice(index, 1);
    writeWishlist(list);
  };

  render() {
    this.button.setAttribute('aria-pressed', String(readWishlist().includes(this.dataset.handle)));
  }
}

if (!customElements.get('olfacy-wishlist-button')) {
  customElements.define('olfacy-wishlist-button', OlfacyWishlistButton);
}

/** Header wishlist count bubble. Hidden when the wishlist is empty. */
class OlfacyWishlistCount extends HTMLElement {
  connectedCallback() {
    this.label = this.querySelector('[data-count]');
    this.render = () => {
      const n = readWishlist().length;
      this.hidden = n === 0;
      if (this.label) this.label.textContent = n > 99 ? '99+' : String(n);
      this.closest('a')?.setAttribute('aria-label', n ? `Wishlist (${n})` : 'Wishlist');
    };
    this.onStorage = (event) => event.key === WISHLIST_KEY && this.render();
    document.addEventListener(WISHLIST_EVENT, this.render);
    window.addEventListener('storage', this.onStorage);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_EVENT, this.render);
    window.removeEventListener('storage', this.onStorage);
  }
}

if (!customElements.get('olfacy-wishlist-count')) {
  customElements.define('olfacy-wishlist-count', OlfacyWishlistCount);
}

/** Live cart count (bottom nav). Updates from Shopify's standard cart event used by the theme. */
class OlfacyCartCount extends HTMLElement {
  connectedCallback() {
    this.onUpdate = (event) => {
      event.promise?.then((result) => {
        const qty = result?.cart?.totalQuantity;
        if (typeof qty === 'number') this.show(qty);
      }).catch(() => {});
    };
    document.addEventListener('shopify:cart:lines-update', this.onUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.onUpdate);
  }

  show(qty) {
    this.textContent = qty > 99 ? '99+' : String(qty);
    this.hidden = qty === 0 && !this.hasAttribute('data-keep-zero');
  }
}

if (!customElements.get('olfacy-cart-count')) {
  customElements.define('olfacy-cart-count', OlfacyCartCount);
}

// Bottom-nav Cart opens the cart drawer when the theme uses one
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-open-cart-drawer]');
  const drawer = document.getElementById('cart-drawer');
  if (!link || !drawer || typeof drawer.open !== 'function') return;
  event.preventDefault();
  drawer.open();
});

/* ---------- Add to Bag ---------- */

const routes = () => window.Theme?.routes || {};

const setBusy = (button, busy) => {
  button.toggleAttribute('disabled', busy);
  button.setAttribute('aria-busy', String(busy));
};

const flashLabel = (label, text, original, ms = 1800) => {
  if (!label) return;
  label.textContent = text;
  setTimeout(() => (label.textContent = original), ms);
};

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-ocard-form')) return;
  // "Buy Now" submits normally (the form has return_to=/checkout) — go straight to checkout
  if (event.submitter?.hasAttribute('data-buy-now')) return;
  event.preventDefault();

  const button = event.submitter?.matches?.('button[type="submit"]') ? event.submitter : form.querySelector('button[type="submit"]');
  const label = form.querySelector('[data-ocard-label]');
  const original = label?.textContent || '';
  if (!button || button.getAttribute('aria-busy') === 'true') return;
  setBusy(button, true);

  const formData = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  // Shopify's standard event module is already used by the theme; load it only when needed.
  let Events = null;
  let deferred = null;
  try {
    Events = await import('@shopify/events');
    deferred = Events.CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new Events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 }],
        promise: deferred.promise,
      })
    );
  } catch {}

  const productId = form.closest('[data-product-id]')?.dataset.productId || '';
  const resolveWith = async (didError, sections) => {
    if (!deferred) return;
    try {
      const cart = await fetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'olfacy-product-card', sourceId: productId, itemCount: 1, productId, sections, didError },
      });
    } catch (error) {
      deferred.reject(error);
    }
  };

  try {
    const response = await fetch(routes().cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      if (Events?.CartErrorEvent) {
        form.dispatchEvent(
          new Events.CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
      }
      await resolveWith(true);
      flashLabel(label, data.description || data.message || 'Unavailable', original, 2500);
      return;
    }

    await resolveWith(false, data.sections);

    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      if (!drawer.isOpen) drawer.open();
    } else {
      flashLabel(label, '✓', original);
    }
  } catch (error) {
    deferred?.reject(error);
    flashLabel(label, 'Try again', original);
  } finally {
    setBusy(button, false);
  }
});
